import { useAuth0 } from '@auth0/auth0-vue'
import { until } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { createWebHistory, createRouter } from 'vue-router'

import {
  ACTIVITY_REPORT_PATH,
  APPOINTMENTS_PATH,
  STRIPE_PATH,
  CALLBACK_PATH,
  FAQ_PATH,
  GOOGLE_CALLBACK_PATH,
  GOOGLE_PATH,
  HOME_PATH,
  HOME_PATH_GUEST,
  IMPORTS_PATH,
  INTEGRATIONS_PATH,
  INVOICES_PATH,
  LEADS_PATH,
  MESSAGES_PATH,
  MY_INVITES_PATH,
  MY_WEBSITES_PATH,
  NOT_FOUND_PATH,
  PATIENT_JOURNEYS_PATH,
  PATIENTS_PATH,
  PATIENTS_SUGGESTIONS_PATH,
  PJ_SUGGESTIONS_PATH,
  PRIVACY_POLICY_PATH,
  PROCEDURES_PATH,
  PROFILE_PATH,
  PUBLIC_REVIEW_PATH,
  REPORTS_PATH,
  SETTINGS_PATH,
  SUGGESTIONS_PATH,
  TEMPLATES_PATH,
  UNAUTH_HOME_PATH,
  USER_ROLES_PATH,
  PROPOSALS_PATH,
  MAILCHIMP_CALLBACK_PATH,
  MAILCHIMP_PATH,
  ZOOM_PATH,
  ZOOM_CALLBACK_PATH,
  PUBLIC_EVENTS_PATH,
  PUBLIC_OFFER_PATH,
  PUBLIC_INVOICE_PATH,
} from '@/constants'
import { useClinicStore } from '@/stores'
import { PermissionTypeEnums } from '@/types'

const routes = [
  {
    path: UNAUTH_HOME_PATH,
    component: () => import('@/pages/unauth/UnauthHomePage.vue'),
  },
  {
    path: PRIVACY_POLICY_PATH,
    name: 'PrivacyPolicy',
    component: () => import('@/pages/privacy-policy/PrivacyPolicyPage.vue'),
  },
  {
    path: `${PUBLIC_REVIEW_PATH}/:slug/:hash?`,
    name: 'PublicReview',
    component: () => import('@/pages/public/review/ReviewPage.vue'),
  },
  {
    path: `${PUBLIC_EVENTS_PATH}/:token`,
    name: 'PublicEvents',
    component: () => import('@/pages/public/events/EventPage.vue'),
  },
  {
    path: `${PUBLIC_OFFER_PATH}/:token`,
    name: 'OfferLink',
    component: () => import('@/pages/public/offer/OfferPage.vue'),
  },
  {
    path: `${PUBLIC_INVOICE_PATH}/:token`,
    name: 'InvoiceLink',
    component: () => import('@/pages/public/invoice/InvoicePage.vue'),
  },
  {
    path: HOME_PATH_GUEST,
    name: 'Onboarding',
    component: () => import('@/pages/onboard/OnBoardViewPage.vue'),
    meta: { requiresAuth: true, emptyLayout: true },
  },
  {
    path: HOME_PATH,
    name: 'Timeline',
    component: () => import('@/pages/timeline/TimelineListPage.vue'),
    meta: { breadcrumb: 'Timeline', requiresAuth: true },
  },
  {
    path: PATIENTS_PATH,
    meta: { breadcrumb: 'Patients', permission: PermissionTypeEnums.VIEW_PATIENTS, requiresAuth: true },
    children: [
      {
        path: '',
        component: () => import('@/pages/patient-list/PatientsListPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ':id',
        name: 'PatientDetails',
        component: () => import('@/pages/patient/PatientViewPage.vue'),
        meta: { breadcrumb: 'Patient Details', permission: PermissionTypeEnums.VIEW_PATIENTS, requiresAuth: true },
      },
    ],
  },
  {
    path: PATIENTS_SUGGESTIONS_PATH,
    meta: { breadcrumb: 'Suggestion for patients', permission: PermissionTypeEnums.VIEW_PATIENTS, requiresAuth: true },
    children: [
      {
        path: '',
        component: () => import('@/pages/patient-suggestions-list/PatientsSuggestionsListPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ':id',
        name: 'PatientSuggestionDetails',
        component: () => import('@/pages/patient/PatientViewPage.vue'),
        meta: { breadcrumb: 'Patient Details', permission: PermissionTypeEnums.VIEW_PATIENTS, requiresAuth: true },
      },
    ],
  },
  {
    path: LEADS_PATH,
    meta: { breadcrumb: 'Leads', requiresAuth: true },
    children: [
      {
        path: '',
        component: () => import('@/pages/lead-list/LeadsListPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ':id',
        name: 'LeadDetails',
        component: () => import('@/pages/lead/LeadViewPage.vue'),
        meta: { breadcrumb: 'Lead Details', requiresAuth: true },
      },
    ],
  },
  {
    path: PATIENT_JOURNEYS_PATH,
    name: 'PatientJourney',
    component: () => import('@/pages/patient-journeys/PatientJourneyPage.vue'),
    meta: { breadcrumb: 'Patient Journeys', requiresAuth: true },
  },
  {
    path: PJ_SUGGESTIONS_PATH,
    name: 'PatientJourneySuggestions',
    component: () => import('@/pages/patient-journeys-suggestions/PatientJourneySuggestionsPage.vue'),
    meta: { breadcrumb: 'Suggestions for Patient Journey', requiresAuth: true },
  },
  {
    path: APPOINTMENTS_PATH,
    name: 'Appointments',
    component: () => import('@/pages/appointments/AppointmentsViewPage.vue'),
    meta: { breadcrumb: 'Appointments', permission: PermissionTypeEnums.VIEW_APPOINTMENTS, requiresAuth: true },
  },
  {
    path: MESSAGES_PATH,
    name: 'Messages',
    component: () => import('@/pages/messages/MessagesViewPage.vue'),
    meta: { breadcrumb: 'Messages', permission: PermissionTypeEnums.VIEW_MESSAGES, requiresAuth: true },
  },
  {
    path: INVOICES_PATH,
    name: 'Invoices',
    component: () => import('@/pages/invoices-list/InvoicesListPage.vue'),
    meta: { breadcrumb: 'Invoices', requiresAuth: true },
  },
  {
    path: PROPOSALS_PATH,
    name: 'Proposals',
    component: () => import('@/pages/proposals-list/ProposalListPage.vue'),
    meta: { breadcrumb: 'Offers', requiresAuth: true },
  },
  {
    path: SUGGESTIONS_PATH,
    name: 'Suggestions',
    component: () => import('@/pages/suggestions/SuggestionsPage.vue'),
    meta: { breadcrumb: 'Suggestions', requiresAuth: true },
  },
  {
    path: REPORTS_PATH,
    meta: { breadcrumb: 'Reports', requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Reports',
        component: () => import('@/pages/reports/ReportsListPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ACTIVITY_REPORT_PATH,
        name: 'ActivityReport',
        component: () => import('@/pages/report/ActivityReportPage.vue'),
        meta: { breadcrumb: 'Activity Report', requiresAuth: true },
      },
    ],
  },
  {
    path: SETTINGS_PATH,
    meta: { breadcrumb: 'Settings', requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Settings',
        component: () => import('@/pages/settings/SettingsListPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: MY_WEBSITES_PATH,
        meta: { breadcrumb: 'My Websites', requiresAuth: true },
        children: [
          {
            path: '',
            name: 'MyWebsites',
            component: () => import('@/pages/my-websites/MyClinicsListPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            path: ':id',
            name: 'MyWebsiteDetails',
            component: () => import('@/pages/my-website/MyClinicViewPage.vue'),
            meta: { breadcrumb: 'Website Details', requiresAuth: true, permission: PermissionTypeEnums.VIEW_CLINICS },
          },
        ],
      },
      {
        path: MY_INVITES_PATH,
        name: 'MyInvites',
        component: () => import('@/pages/my-invites/UserInvitesListPage.vue'),
        meta: { breadcrumb: 'My Invitations', requiresAuth: true },
      },
      {
        path: USER_ROLES_PATH,
        name: 'UserRoles',
        component: () => import('@/pages/user-roles/UserRolesListPage.vue'),
        meta: { breadcrumb: 'User Roles', permission: PermissionTypeEnums.INVITE_USERS, requiresAuth: true },
      },
      {
        path: INTEGRATIONS_PATH,
        meta: { breadcrumb: 'Integrations', permission: PermissionTypeEnums.EDIT_CLINICS, requiresAuth: true },
        children: [
          {
            path: '',
            name: 'Integrations',
            component: () => import('@/pages/integrations/IntegrationsListPage.vue'),
            meta: { permission: PermissionTypeEnums.EDIT_CLINICS, requiresAuth: true },
          },
          {
            path: STRIPE_PATH,
            name: 'Stripe',
            component: () => import('@/pages/integrations/stripe/StripePage.vue'),
            meta: { breadcrumb: 'Stripe', permission: PermissionTypeEnums.EDIT_CLINICS, requiresAuth: true },
          },
          {
            path: GOOGLE_PATH,
            name: 'GoogleAds',
            component: () => import('@/pages/integrations/google-ads/GoogleAdsPage.vue'),
            meta: { breadcrumb: 'Google Ads', permission: PermissionTypeEnums.EDIT_CLINICS, requiresAuth: true },
          },
          {
            path: GOOGLE_CALLBACK_PATH,
            name: 'GoogleAdsCallback',
            component: () => import('@/pages/integrations/google-ads/GoogleAdsCallback.vue'),
            meta: { permission: PermissionTypeEnums.EDIT_CLINICS, requiresAuth: true, emptyLayout: true },
          },
          {
            path: MAILCHIMP_PATH,
            name: 'MailChimp',
            component: () => import('@/pages/integrations/mailchimp/MailChimpPage.vue'),
            meta: { breadcrumb: 'Mailсhimp', permission: PermissionTypeEnums.EDIT_CLINICS, requiresAuth: true },
          },
          {
            path: MAILCHIMP_CALLBACK_PATH,
            name: 'MailChimpCallback',
            component: () => import('@/pages/integrations/mailchimp/MailChimpCallback.vue'),
            meta: { permission: PermissionTypeEnums.EDIT_CLINICS, requiresAuth: true, emptyLayout: true },
          },
          {
            path: ZOOM_PATH,
            name: 'Zoom',
            component: () => import('@/pages/integrations/zoom/ZoomPage.vue'),
            meta: { breadcrumb: 'Zoom', permission: PermissionTypeEnums.EDIT_CLINICS, requiresAuth: true },
          },
          {
            path: ZOOM_CALLBACK_PATH,
            name: 'ZoomCallback',
            component: () => import('@/pages/integrations/zoom/ZoomCallback.vue'),
            meta: { permission: PermissionTypeEnums.EDIT_CLINICS, requiresAuth: true, emptyLayout: true },
          },
        ],
      },
      {
        path: PROFILE_PATH,
        name: 'Profile',
        component: () => import('@/pages/profile/ProfilePage.vue'),
        meta: { breadcrumb: 'My Profile', requiresAuth: true },
      },
      {
        path: PROCEDURES_PATH,
        name: 'Procedures',
        component: () => import('@/pages/procedures/ProceduresViewPage.vue'),
        meta: { breadcrumb: 'Procedures', requiresAuth: true },
      },
      {
        path: IMPORTS_PATH,
        meta: { breadcrumb: 'Imports', requiresAuth: true },
        children: [
          {
            path: '',
            component: () => import('@/pages/imports-list/ImportsListPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            path: ':id',
            name: 'ImportDetails',
            component: () => import('@/pages/import/ImportViewPage.vue'),
            meta: { breadcrumb: 'Import Details', requiresAuth: true },
          },
        ],
      },
      {
        path: FAQ_PATH,
        name: 'FAQ',
        component: () => import('@/pages/faq-list/FaqListPage.vue'),
        meta: { breadcrumb: 'FAQ', requiresAuth: true, permission: PermissionTypeEnums.VIEW_CLINICS },
      },
      {
        path: TEMPLATES_PATH,
        name: 'Templates',
        component: () => import('@/pages/templates-list/TemplatesListPage.vue'),
        meta: { breadcrumb: 'Templates', requiresAuth: true },
      },
    ],
  },
  { path: CALLBACK_PATH, name: 'Callback', component: () => import('@/pages/auth/AuthCallback.vue') },
  { path: NOT_FOUND_PATH, name: 'NotFound', component: () => null },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, from) => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0()

  // Wait for Auth0 SDK readiness
  await until(isLoading).toBe(false)

  if (to.name === 'Callback') {
    // Optional: handle only silent-auth-specific errors to escalate to interactive login
    const err = String(to.query?.error || '')
    const silentAuthErrors = ['login_required', 'consent_required', 'interaction_required']
    if (silentAuthErrors.includes(err)) {
      await loginWithRedirect({
        appState: { targetUrl: from.fullPath },
      })
      return false
    }
    // For non-error or other errors, let the callback component handle it
    return true
  }

  if (isAuthenticated.value && to.path === UNAUTH_HOME_PATH) {
    return { path: HOME_PATH, replace: true }
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  if (requiresAuth && !isAuthenticated.value) {
    // Try silent first
    await loginWithRedirect({
      authorizationParams: { prompt: 'none' },
      appState: { targetUrl: to.fullPath },
    })
    return false
  }

  if (!isAuthenticated.value && !requiresAuth) {
    return to.name === 'NotFound' ? { path: UNAUTH_HOME_PATH, replace: true } : true
  }

  const clinicsStore = useClinicStore()
  const { permissions, isSuperUser, isFetchingClinic } = storeToRefs(clinicsStore)

  await until(() => permissions.value !== null || !isFetchingClinic.value).toBe(true)

  // Get the most specific permission requirement (child overrides parent)
  const requiredPermission = [...to.matched]
    .reverse() // Start from most specific route
    .find(record => record.meta.permission)?.meta.permission

  const hasPermission = isSuperUser.value || !requiredPermission || permissions.value?.[requiredPermission]

  // Redirect if no permission or 404
  if (!hasPermission || to.name === 'NotFound') {
    return { path: hasPermission ? HOME_PATH : HOME_PATH_GUEST, replace: true }
  }

  return true
})

export default router
